import { useCallback, useEffect, useRef, useState } from "react";

import { listUsers, updateUserStatus } from "./api";
import type { User, UserStatus, UserStatusFilter } from "./types";

const PAGE_SIZE = 50;

export interface UseUsersReturn {
  users: readonly User[];
  total: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  filter: UserStatusFilter;
  setFilter: (next: UserStatusFilter) => void;
  refresh: () => void;
  loadMore: () => void;
  changeStatus: (id: string, status: UserStatus) => Promise<void>;
  lastError: { userId: string; message: string } | null;
  clearLastError: () => void;
}

export function useUsers(): UseUsersReturn {
  const [users, setUsers] = useState<readonly User[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<UserStatusFilter>("all");
  const [lastError, setLastError] = useState<{ userId: string; message: string } | null>(null);

  /**
   * Monotonic request id. Every list fetch bumps it; stale responses are discarded
   * so an older `listUsers` response can't overwrite newer state after a rapid
   * filter change or repeated refresh.
   */
  const requestIdRef = useRef(0);

  const load = useCallback(async (currentFilter: UserStatusFilter) => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    try {
      const params =
        currentFilter === "all"
          ? { limit: PAGE_SIZE, offset: 0 }
          : { status: currentFilter, limit: PAGE_SIZE, offset: 0 };
      const result = await listUsers(params);
      if (requestId !== requestIdRef.current) return;
      setUsers(result.users);
      setTotal(result.total);
      setError(null);
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      setError(e instanceof Error ? e.message : "Failed to load users");
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(filter);
  }, [filter, load]);

  const refresh = useCallback(() => {
    void load(filter);
  }, [filter, load]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || isLoading) return;
    const requestId = ++requestIdRef.current;
    setIsLoadingMore(true);
    try {
      const offset = users.length;
      const params =
        filter === "all"
          ? { limit: PAGE_SIZE, offset }
          : { status: filter, limit: PAGE_SIZE, offset };
      const result = await listUsers(params);
      if (requestId !== requestIdRef.current) return;
      setUsers((prev) => [...prev, ...result.users]);
      setTotal(result.total);
      setError(null);
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      setError(e instanceof Error ? e.message : "Failed to load more users");
    } finally {
      if (requestId === requestIdRef.current) setIsLoadingMore(false);
    }
  }, [filter, isLoading, isLoadingMore, users.length]);

  const changeStatus = useCallback(
    async (id: string, status: UserStatus) => {
      try {
        const updated = await updateUserStatus(id, status);
        setLastError(null);
        if (filter !== "all" && updated.access_status !== filter) {
          setUsers((prev) => prev.filter((u) => u.id !== id));
          setTotal((prev) => Math.max(0, prev - 1));
        } else {
          setUsers((prev) =>
            prev.map((u) => (u.id === id ? { ...u, access_status: updated.access_status } : u))
          );
        }
      } catch (e) {
        setLastError({
          userId: id,
          message: e instanceof Error ? e.message : "Failed to update status",
        });
      }
    },
    [filter]
  );

  const clearLastError = useCallback(() => {
    setLastError(null);
  }, []);

  return {
    users,
    total,
    isLoading,
    isLoadingMore,
    hasMore: users.length < total,
    error,
    filter,
    setFilter,
    refresh,
    loadMore,
    changeStatus,
    lastError,
    clearLastError,
  };
}
